import { useStorage } from '@vueuse/core';
import type {
  StorageKey,
  StorageSetterCookie,
  StorageSetterGeneral,
} from '~/interfaces/dto/storage/storage.interface';

const storageCache: Record<string, Ref> = {};

export const useStorageService = () => {
  const getStorageRef = <T>(
    key: string,
    initialValue: T,
    storage: Storage,
  ): Ref<T> => {
    const cacheKey = `${storage === localStorage ? 'ls' : 'ss'}:${key}`;
    if (!storageCache[cacheKey]) {
      storageCache[cacheKey] = useStorage<T>(key, initialValue, storage);
    }
    return storageCache[cacheKey];
  };
  const isJSON = (str: string): boolean => {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  };
  const validateStorageAccess = async () => {
    if (import.meta.client && typeof document.hasStorageAccess === 'function')
      return await document?.hasStorageAccess();
    return false;
  };
  const requestStorageAccess = async () => {
    if (
      import.meta.client &&
      typeof document.requestStorageAccess === 'function' &&
      window?.isSecureContext
    )
      return await document?.requestStorageAccess().then(
        () => {},
        () => {
          console.error('Storage Access denied');
        },
      );
  };
  const setCookie = ({ key, data, expiry }: StorageSetterCookie): void => {
    try {
      const cookie = useCookie;
      const domain = (() => {
        const d = document.location.hostname;
        if (d.startsWith('www') || d.startsWith('en')) {
          cookie(key, { domain: d, watch: false }).value = null;
          return d.replace('www.', '').replace('en.', '');
        }
        return d;
      })();

      if (typeof data === 'object') data = JSON.stringify(data);
      const date = new Date();
      const expires = !!expiry
        ? { expires: new Date(date.getTime() + expiry * 24 * 60 * 60 * 1000) }
        : {};

      cookie(key, {
        path: '/',
        domain,
        secure: true,
        sameSite: 'lax',
        ...expires,
      }).value = data;
    } catch (e) {
      console.error(e);
    }
  };
  const getCookie = <T>({ key }: StorageKey): T => {
    return (useCookie(key).value || '') as T;
  };
  const setLocalStorage = ({ key, data }: StorageSetterGeneral): void => {
    if (typeof window !== 'undefined' && !!localStorage) {
      if (typeof data === 'object') data = JSON.stringify(data);
      const s = getStorageRef(key, data, localStorage);
      s.value = data;
      const event = new StorageEvent('storage', {
        key,
        newValue: data,
      });
      dispatchEvent(event);
    }
  };
  const getLocalStorage = <T>({ key }: StorageKey): T => {
    try {
      if (typeof window !== 'undefined' && !!localStorage) {
        if (validateStorageAccess()) requestStorageAccess();
        const s = getStorageRef<T>(key, null, localStorage);
        const data = s.value;
        if (typeof data === 'string' && isJSON(data)) {
          try {
            return JSON.parse(data) as T;
          } catch (e) {
            return data as T;
          }
        }
        return data as T;
      }
    } catch (e) {
      console.error(e);
    }
  };
  const removeLocalStorage = ({ key }: StorageKey) => {
    const s = getStorageRef(key, null, localStorage);
    s.value = null;
  };
  const setSessionStorage = ({ key, data }: StorageSetterGeneral) => {
    const s = getStorageRef(key, data, sessionStorage);
    if (typeof window !== 'undefined' && !!sessionStorage) {
      if (typeof data === 'object') data = JSON.stringify(data);
      s.value = data;
    }
  };
  const getSessionStorage = <T>({ key }: StorageKey): T => {
    try {
      if (typeof window !== 'undefined' && !!sessionStorage) {
        if (validateStorageAccess()) requestStorageAccess();
        const s = getStorageRef<T>(key, null, sessionStorage);
        const data = s.value;
        if (typeof data === 'string' && isJSON(data)) {
          try {
            return JSON.parse(data) as T;
          } catch (e) {
            return data as T;
          }
        }
        return data as T;
      }
    } catch (e) {
      console.error(e);
    }
  };
  const removeSessionStorage = ({ key }: StorageKey) => {
    const s = getStorageRef(key, null, sessionStorage);
    s.value = null;
  };
  const setUniversalStorage = ({ key, data, expiry }: StorageSetterCookie) => {
    setCookie({ key, data, expiry });
    setLocalStorage({ key, data });
    setSessionStorage({ key, data });
  };
  const getUniversalStorage = <T>({ key }: StorageKey): T => {
    return (getCookie({ key }) ||
      getLocalStorage({ key }) ||
      getSessionStorage({ key })) as T;
  };

  return {
    isJSON,
    setCookie,
    getCookie,
    removeCookie: ({ key }: StorageKey) => setCookie({ key, data: '' }),
    removeCookies: (keys: string[]) =>
      keys.forEach((key) => setCookie({ key, data: '' })),
    setLocalStorage,
    getLocalStorage,
    removeLocalStorage,
    removeMultipleFromLocalStorage: (keys: string[]) =>
      keys.forEach((k) => removeLocalStorage({ key: k })),
    setSessionStorage,
    getSessionStorage,
    removeSessionStorage,
    removeMultipleFromSessionStorage: (keys: string[]) =>
      keys.forEach((k) => removeSessionStorage({ key: k })),
    setUniversalStorage,
    getUniversalStorage,
    removeUniversalStorage: ({ key }: StorageKey) => {
      setCookie({ key, data: '' });
      removeLocalStorage({ key });
      removeSessionStorage({ key });
    },
  };
};
